import { CheckOutlined } from "@ant-design/icons"
import { Flex, Typography, theme } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  type OnboardingItem,
  type OnboardingItemId
} from "../content/onboarding.ts"

const { Text } = Typography

interface GuideSetupStepperProps {
  items: OnboardingItem[]
  isItemComplete: (item: OnboardingItem) => boolean
  currentItemId: OnboardingItemId | null
  onSelect: (id: OnboardingItemId) => void
}

/**
 * "Where am I" and the entry point into setup — every step is selectable in
 * any order and a completed step stays real and reviewable, it doesn't just
 * vanish behind the current one. Selecting a step navigates straight to its
 * real settings page and opens the Setup Assistant there; there is no
 * intermediate "here's what to do" card on Guide Home any more — the
 * stepper itself, plus the Setup Assistant once a step is open, carry that
 * job. Clicking never changes completion state by itself, so revisiting a
 * done step can't un-complete it.
 */
export const GuideSetupStepper = ({
  items,
  isItemComplete,
  currentItemId,
  onSelect
}: GuideSetupStepperProps) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  return (
    <Flex align="start" className="w-full overflow-x-auto py-2">
      {items.map((item, index) => {
        const complete = isItemComplete(item)
        const isCurrent = item.id === currentItemId

        const circleStyle = complete
          ? { background: token.colorPrimary, borderColor: token.colorPrimary }
          : isCurrent
            ? {
                borderColor: token.colorPrimary,
                color: token.colorPrimary,
                borderWidth: 2
              }
            : { borderColor: token.colorBorder, color: token.colorTextTertiary }

        return (
          <Flex
            key={item.id}
            align="start"
            className={index === 0 ? "" : "flex-1"}
          >
            {index > 0 && (
              <div
                className="mx-2 mt-5 h-0.5 min-w-10 flex-1"
                style={{
                  background:
                    complete || isCurrent
                      ? token.colorPrimary
                      : token.colorBorderSecondary
                }}
              />
            )}
            <button
              type="button"
              onClick={() => onSelect(item.id)}
              className="group flex shrink-0 cursor-pointer flex-col items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-black/[0.035] focus-visible:outline focus-visible:outline-2"
              style={{ outlineColor: token.colorPrimary }}
              aria-label={t(
                complete
                  ? "guide.stepper.reviewStep"
                  : "guide.stepper.openStep",
                { step: t(item.labelKey) }
              )}
              aria-current={isCurrent ? "step" : undefined}
            >
              {/* Reserve the row even when not current, so the circle below
                  never shifts vertically depending on which step is next. */}
              <Text
                className="text-[10px] leading-none font-semibold tracking-wide uppercase"
                style={{
                  color: isCurrent ? token.colorPrimary : "transparent"
                }}
              >
                {isCurrent ? t("guide.stepper.nextLabel") : "·"}
              </Text>
              <Flex
                align="center"
                justify="center"
                className="mt-1 size-10 shrink-0 rounded-full border-2 text-sm font-semibold"
                style={circleStyle}
              >
                {complete ? (
                  <CheckOutlined
                    className="text-base"
                    style={{ color: "#fff" }}
                  />
                ) : (
                  index + 1
                )}
              </Flex>
              <Text
                className="max-w-[110px] text-center text-sm leading-tight transition-colors group-hover:font-medium"
                strong={isCurrent}
                type={complete || isCurrent ? undefined : "secondary"}
              >
                {t(item.labelKey)}
              </Text>
              <Text
                className="text-[11px] leading-none opacity-0 transition-opacity group-hover:opacity-100"
                style={{ color: token.colorPrimary }}
              >
                {t("guide.stepper.openArrow")}
              </Text>
            </button>
          </Flex>
        )
      })}
    </Flex>
  )
}
